/**
 * iter240 — Dialogue d'export CSV des clients.
 * Colonnes à cocher (avec préréglages), et export limité aux filtres actifs
 * de la liste (type privé/entreprise/assurance, statut, recherche…).
 */
import { useCallback, useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Download, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const PRESETS = {
  mailing: ["nom_affiche", "type", "email", "email_factures"],
  postal: ["nom_affiche", "adresse", "npa", "localite", "pays"],
  telephone: ["nom_affiche", "tel_principal", "tel_secondaire"],
};

export default function ClientsExportDialog({ open, onClose, filtres = {}, total = 0, limiteSelection = false }) {
  const [colonnes, setColonnes] = useState([]);
  const [selection, setSelection] = useState([]);
  const [avecEmail, setAvecEmail] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    if (!open) return;
    try {
      const { data } = await api.get("/clients/export-colonnes");
      setColonnes(data.colonnes || []);
      setSelection(data.defaut || []);
    } catch {
      toast.error("Impossible de charger les colonnes");
    }
  }, [open]);

  useEffect(() => { load(); }, [load]);

  const toggle = (key) =>
    setSelection((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]));

  const download = async () => {
    if (selection.length === 0) { toast.error("Sélectionnez au moins une colonne"); return; }
    setBusy(true);
    try {
      const params = { ...filtres, colonnes: selection.join(",") };
      if (avecEmail) params.avec_email = true;
      const response = await api.get("/clients/export-csv", { params, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = "clients_carmain.csv";
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success("Export téléchargé");
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Export impossible");
    } finally {
      setBusy(false);
    }
  };

  const nbFiltres = Object.values(filtres).filter(Boolean).length;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && !busy && onClose?.()}>
      <DialogContent className="sm:max-w-lg" data-testid="clients-export-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Download className="w-5 h-5 text-[#52a675]" /> Export CSV des clients
          </DialogTitle>
          <DialogDescription>
            {limiteSelection
              ? `Seules les ${total} fiche${total > 1 ? "s" : ""} cochée${total > 1 ? "s" : ""} dans la liste seront exportées.`
              : nbFiltres > 0
                ? `${total} client${total > 1 ? "s" : ""} correspondant aux filtres actifs seront exportés.`
                : `Les ${total} clients seront exportés. Utilisez les filtres de la liste pour restreindre (type, statut, recherche).`}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="flex flex-wrap gap-1.5">
            <button
              type="button"
              onClick={() => setSelection(colonnes.map((c) => c.key))}
              className="px-2 py-1 rounded-full border border-gray-200 text-[11px] text-gray-600 hover:bg-gray-50"
              data-testid="export-preset-tout"
            >
              Tout cocher
            </button>
            <button
              type="button"
              onClick={() => setSelection([])}
              className="px-2 py-1 rounded-full border border-gray-200 text-[11px] text-gray-600 hover:bg-gray-50"
              data-testid="export-preset-rien"
            >
              Tout décocher
            </button>
            <button
              type="button"
              onClick={() => { setSelection(PRESETS.mailing); setAvecEmail(true); }}
              className="px-2 py-1 rounded-full border border-gray-200 text-[11px] text-gray-600 hover:bg-gray-50"
              data-testid="export-preset-mailing"
            >
              Adresses e-mail
            </button>
            <button
              type="button"
              onClick={() => setSelection(PRESETS.postal)}
              className="px-2 py-1 rounded-full border border-gray-200 text-[11px] text-gray-600 hover:bg-gray-50"
              data-testid="export-preset-postal"
            >
              Adresses postales
            </button>
            <button
              type="button"
              onClick={() => setSelection(PRESETS.telephone)}
              className="px-2 py-1 rounded-full border border-gray-200 text-[11px] text-gray-600 hover:bg-gray-50"
              data-testid="export-preset-tel"
            >
              Téléphones
            </button>
          </div>

          <div>
            <Label className="text-xs uppercase tracking-wider text-gray-500 font-semibold">
              Colonnes ({selection.length})
            </Label>
            <div
              className="mt-1.5 max-h-64 overflow-y-auto border border-gray-200 rounded-lg divide-y divide-gray-100"
              data-testid="export-colonnes-list"
            >
              {colonnes.map((c) => (
                <label
                  key={c.key}
                  className="flex items-center gap-2 px-3 py-2 text-sm text-gray-700 cursor-pointer hover:bg-gray-50"
                >
                  <input
                    type="checkbox"
                    checked={selection.includes(c.key)}
                    onChange={() => toggle(c.key)}
                    data-testid={`export-col-${c.key}`}
                  />
                  {c.label}
                </label>
              ))}
            </div>
          </div>

          <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
            <input
              type="checkbox"
              checked={avecEmail}
              onChange={(e) => setAvecEmail(e.target.checked)}
              data-testid="export-avec-email"
            />
            Uniquement les clients qui ont une adresse e-mail
          </label>

          <Button
            onClick={download}
            disabled={busy || selection.length === 0}
            className="w-full bg-[#52a675] hover:bg-[#3d8a5c]"
            data-testid="export-download"
          >
            {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
            Télécharger le CSV
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
